import { Button, Flex, Layout, Skeleton } from 'antd'
import dayjs from 'dayjs'
import React, { useMemo } from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import CustomInput from 'src/app/shared/components/common/input'
import CustomSelect from 'src/app/shared/components/common/select'
import CustomSwitch from 'src/app/shared/components/common/switch'
import { ActivationWarning } from 'src/app/shared/components/common/tag/ActivationWarning'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { useWindowSize } from 'src/app/shared/hooks/useWindowSize'
import { inputNumber } from 'src/app/shared/utils'
import { getLookupValues } from 'src/app/store/masterSetup'
import ACHForm from '../components/ACHForm'
import CheckForm from '../components/CheckForm'
import '../styles/index.scss'
import { useEditFormRules } from '../utils'
import { useViewAccountContext } from './index.context'

const ViewAccountContent: React.FC = () => {
  const { t } = useTranslation()
  const rules = useEditFormRules()
  const lookupValues = useSelector(getLookupValues)
  const { form, achExceptionProcessingType, checkExceptionProcessingType, loading, isFetched, client } = useViewAccountContext()
  const { control } = useMemo(() => form, [form])
  const navigate = useNavigate()
  const { mode } = useWindowSize()

  if (!isFetched) {
    return (
      <Layout.Content style={{ padding: '24px 24px 0 24px' }}>
        <Skeleton />
      </Layout.Content>
    )
  }

  return (
    <Layout.Content style={{ padding: '24px 24px 0 24px' }}>
      {client?.activationDate && <ActivationWarning activationDate={dayjs(client.activationDate)} />}
      <form>
        <Flex
          style={{
            flexDirection: 'column',
            gap: 24,
          }}
        >
          <Controller
            control={control}
            name={'clientName'}
            render={({ field, fieldState }) => (
              <CustomSelect
                label={t('AccountSetup:new_account_form.title.clientName')}
                placeholder={t('AccountSetup:new_account_form.placeholder.clientName')}
                required
                value={!field.value ? null : field.value}
                onChange={field.onChange}
                onBlur={async (event) => {
                  field.onBlur()
                  await form.trigger('clientName')
                }}
                options={[]}
                error={fieldState.error}
                loading={loading}
                disabled
              />
            )}
            rules={rules.clientName}
          />
          <Flex
            style={{
              gap: 24,
              flexDirection: mode === 'mobile' ? 'column' : undefined,
            }}
          >
            <Controller
              control={control}
              name={'accountName'}
              render={({ field, fieldState }) => (
                <CustomInput
                  label={t('AccountSetup:new_account_form.title.accountName')}
                  placeholder={t('AccountSetup:new_account_form.placeholder.accountName')}
                  required
                  value={field.value}
                  className='ant-input-view-only'
                  onChange={field.onChange}
                  onBlur={async (event) => {
                    field.onBlur()
                    await form.trigger('accountName')
                  }}
                  error={fieldState.error}
                  maxLength={(rules.accountName.maxLength as any).value}
                  disabled
                />
              )}
              rules={rules.accountName}
            />
            <Controller
              control={control}
              name={'accountNumber'}
              render={({ field, fieldState }) => (
                <CustomInput
                  label={t('AccountSetup:new_account_form.title.accountNumber')}
                  placeholder={t('AccountSetup:new_account_form.placeholder.accountNumber')}
                  required
                  className='ant-input-view-only'
                  value={field.value}
                  onChange={(e) => field.onChange(inputNumber(e), { shouldValidate: true })}
                  onBlur={async () => {
                    field.onBlur()
                    await form.trigger('accountNumber')
                  }}
                  error={fieldState.error}
                  maxLength={(rules.accountNumber.maxLength as any).value}
                  disabled
                />
              )}
              rules={rules.accountNumber}
            />
          </Flex>
          <Flex
            style={{
              gap: 24,
              flexDirection: mode === 'mobile' ? 'column' : undefined,
            }}
          >
            <Controller
              control={control}
              name={'chargeAccountNumber'}
              render={({ field, fieldState }) => (
                <CustomInput
                  label={t('AccountSetup:new_account_form.title.chargeAccountNumber')}
                  placeholder={t('AccountSetup:new_account_form.placeholder.chargeAccountNumber')}
                  value={field.value}
                  className='ant-input-view-only'
                  onChange={(e) => field.onChange(inputNumber(e), { shouldValidate: true })}
                  onBlur={async () => {
                    field.onBlur()
                    await form.trigger('chargeAccountNumber')
                  }}
                  error={fieldState.error}
                  maxLength={(rules.chargeAccountNumber.maxLength as any).value}
                  disabled
                  required
                />
              )}
              rules={rules.chargeAccountNumber}
            />
            <Controller
              control={control}
              name={'displayNotes'}
              render={({ field, fieldState }) => (
                <CustomInput
                  label={t('AccountSetup:new_account_form.title.displayNotes')}
                  placeholder={t('AccountSetup:new_account_form.placeholder.displayNotes')}
                  value={field.value}
                  onChange={field.onChange}
                  className='ant-input-view-only'
                  onBlur={field.onBlur}
                  error={fieldState.error}
                  maxLength={(rules.displayNotes.maxLength as any).value}
                  disabled
                />
              )}
              rules={rules.displayNotes}
            />
          </Flex>
          <Controller
            control={control}
            name={'serviceTypeParameter'}
            render={({ field, fieldState }) => (
              <CustomSelect
                label={t('AccountSetup:new_account_form.title.serviceTypeParameter')}
                placeholder={t('AccountSetup:new_account_form.placeholder.serviceTypeParameter')}
                value={!field.value ? null : field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error}
                options={lookupValues?.SrvChg}
                maxLength={(rules.serviceTypeParameter.maxLength as any).value}
                disabled
                loading={loading}
              />
            )}
            rules={rules.serviceTypeParameter}
          />

          <CheckForm
            checkExceptionProcessingType={checkExceptionProcessingType}
            control={control as any}
            form={form as any}
            loading={loading}
            disabledForm
          />

          <ACHForm
            achExceptionProcessingType={achExceptionProcessingType}
            control={control as any}
            form={form as any}
            loading={loading}
            disabledForm
          />

          <Flex
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Controller
              control={form.control}
              name='isArchiveAccount'
              render={({ field, fieldState }) => (
                <CustomSwitch
                  label={t('AccountSetup:new_account_form.title.archiveAccount')}
                  checked={!!field.value}
                  onChange={field.onChange}
                  error={fieldState.error}
                  disabled
                />
              )}
            />
            <Controller
              control={form.control}
              name='createdAt'
              render={({ field, fieldState }) => (
                <CustomInput
                  value={field.value}
                  className='ant-input-view-only'
                  label={t('AccountSetup:new_account_form.title.createdAt')}
                  onChange={field.onChange}
                  error={fieldState.error}
                  disabled
                />
              )}
            />
          </Flex>
          <Flex
            style={{
              justifyContent: 'flex-end',
              gap: 8,
            }}
            className='button-group-container'
          >
            <Button
              type='default'
              onClick={() =>
                navigate(menuUrls.accountSetup, {
                  replace: true,
                })
              }
              disabled={loading}
              className='new-account-cancel-button'
            >
              {t('common:back_btn')}
            </Button>
          </Flex>
        </Flex>
      </form>
    </Layout.Content>
  )
}

export default ViewAccountContent
